import { useState, useRef, useEffect } from "react";

import { Eye, EyeOff, Loader2, Clock } from "lucide-react";

import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { useStepUpAuth } from "@/hooks/useStepUpAuth";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { cn } from "@/lib/utils";

import { StepUpAuthDialog } from "@/components/security/StepUpAuthDialog";

interface RedactedEmailProps {
  userId: string;
  redactedEmail: string;
  className?: string;
  sessionTimeoutMinutes?: number;
}

const SESSION_STORAGE_KEY = 'admin_last_2fa_verification';
const SESSION_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes default

export const RedactedEmail = ({ 
  userId, 
  redactedEmail, 
  className,
  sessionTimeoutMinutes = 15 
}: RedactedEmailProps) => {
  const [isRevealed, setIsRevealed] = useState(false);
  const [fullEmail, setFullEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const sessionCheckInterval = useRef<NodeJS.Timeout | null>(null);
  const timeoutMs = sessionTimeoutMinutes * 60 * 1000;
  const { execute: invokeEdgeFunction } = useEdgeFunction({ auth: true });
  
  // Use StepUpAuth hook instead of legacy 2FA
  const { 
    requireStepUp, 
    showStepUpDialog, 
    setShowStepUpDialog, 
    handleVerified, 
    currentActionType, 
    stepUpData 
  } = useStepUpAuth();

  useEffect(() => {
    // Check session validity periodically
    sessionCheckInterval.current = setInterval(() => {
      checkSessionValidity();
    }, 60000); // Check every minute

    return () => {
      if (sessionCheckInterval.current) {
        clearInterval(sessionCheckInterval.current);
      }
    };
  }, [timeoutMs]);

  const checkSessionValidity = (): boolean => {
    const lastVerification = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!lastVerification) {
      setSessionExpired(true);
      return false;
    }

    const timeSinceVerification = Date.now() - parseInt(lastVerification, 10);
    const isExpired = timeSinceVerification > timeoutMs;
    
    if (isExpired) {
      setSessionExpired(true);
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      if (isRevealed) {
        setIsRevealed(false);
        setFullEmail(null);
        toast.highImpact('Session expired. Please verify identity again to view sensitive data.');
      }
      return false;
    }

    setSessionExpired(false);
    return true;
  };

  const fetchFullEmail = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await invokeEdgeFunction('get-full-email', {
        body: { userId }
      });

      if (error) {
        if (error.message?.includes('2FA must be enabled')) {
          toast.error('You must enable 2FA to view full email addresses');
        } else if (error.message?.includes('Authentication required')) {
          toast.error('Session expired. Please refresh and try again.');
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
          setSessionExpired(true);
        } else {
          toast.error('Failed to retrieve email address');
        }
        console.error('Error fetching full email:', error);
        return;
      }

      setFullEmail(data.email);
      setIsRevealed(true);
      
      // Update session timestamp on successful access
      sessionStorage.setItem(SESSION_STORAGE_KEY, Date.now().toString());
      setSessionExpired(false);
    } catch (error) {
      console.error('Error fetching full email:', error);
      toast.error('Failed to retrieve email address');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReveal = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isRevealed) {
      setIsRevealed(false);
      return;
    }

    // Check if session is still valid
    if (!checkSessionValidity()) {
      // Session expired - trigger step-up revalidation flow
      await requireStepUp('view_sensitive_data', fetchFullEmail);
      return;
    }

    // Session valid - fetch email directly
    await fetchFullEmail();
  };

  const displayEmail = isRevealed && fullEmail ? fullEmail : redactedEmail;
  const shouldShowWarning = sessionExpired && !isRevealed;

  return (
    <>
      <div className={cn("flex items-center gap-2", className)}>
        <span className="font-mono text-sm">{displayEmail}</span>
        {shouldShowWarning && (
          <div title="Session expired">
            <Clock className="h-3 w-3 text-yellow-600" />
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={handleReveal}
          disabled={isLoading}
          title={
            sessionExpired 
              ? "Session expired - verify identity again" 
              : isRevealed 
              ? "Hide email" 
              : "Show full email (requires verification)"
          }
        >
          {isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : isRevealed ? (
            <EyeOff className="h-3 w-3" />
          ) : (
            <Eye className="h-3 w-3" />
          )}
        </Button>
      </div>

      {/* Step-Up Verification Dialog */}
      <StepUpAuthDialog
        open={showStepUpDialog}
        onOpenChange={setShowStepUpDialog}
        onVerified={handleVerified}
        actionType={currentActionType}
        reason={stepUpData?.reason}
        anomalyScore={stepUpData?.anomaly_score}
      />
    </>
  );
};
